import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api/client';
import { toast } from 'sonner';
import { Upload, Trash2, GripVertical, Save, RotateCcw, Eye, ArrowUp, ArrowDown } from 'lucide-react';
import { AdminLayout } from '@/components/layouts/admin-layout';
import { MediaImage } from '@/components/ui/media-image';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

const MAX_SLIDES = 2;

interface HeroSlide {
  mobile: string;
  desktop: string;
  description?: string;
}

interface HeroData {
  slides: HeroSlide[];
  rotationIntervalMs: number;
  overlayOpacity: number;
  isEnabled: boolean;
}

const DEFAULT_HERO: HeroData = {
  slides: [],
  rotationIntervalMs: 6000,
  overlayOpacity: 0.3,
  isEnabled: false,
};

type HeroImageVariant = 'mobile' | 'desktop';

async function optimizeHeroImage(file: File, variant: HeroImageVariant): Promise<File> {
  const maxWidth = variant === 'mobile' ? 1080 : 1920;
  const maxHeight = variant === 'mobile' ? 1920 : 1080;
  const quality = variant === 'mobile' ? 0.8 : 0.82;

  if (typeof window === 'undefined') {
    return file;
  }

  const imageUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image for optimization'));
      img.src = imageUrl;
    });

    const scale = Math.min(maxWidth / image.width, maxHeight / image.height, 1);
    const outputWidth = Math.max(1, Math.round(image.width * scale));
    const outputHeight = Math.max(1, Math.round(image.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      return file;
    }

    context.drawImage(image, 0, 0, outputWidth, outputHeight);

    const optimizedBlob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/webp', quality);
    });

    if (!optimizedBlob || optimizedBlob.size >= file.size) {
      return file;
    }

    const baseName = file.name.replace(/\.[^.]+$/, '');
    return new File([optimizedBlob], `${baseName}.webp`, {
      type: 'image/webp',
      lastModified: Date.now(),
    });
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}

async function uploadImageFile(file: File, folder: string): Promise<string | null> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folder);

  const response = await api.post<{ url: string; key: string }>(
    '/api/admin/upload',
    formData,
  );

  if (response.success && response.data?.url) {
    return response.data.url;
  }

  toast.error(response.error?.message || 'Upload failed');
  return null;
}

interface SlideCardProps {
  slide: HeroSlide;
  index: number;
  total: number;
  onUpdate: (updated: HeroSlide) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function SlideCard({ slide, index, total, onUpdate, onRemove, onMoveUp, onMoveDown }: SlideCardProps) {
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const desktopInputRef = useRef<HTMLInputElement>(null);
  const [uploadingMobile, setUploadingMobile] = useState(false);
  const [uploadingDesktop, setUploadingDesktop] = useState(false);

  const handleMobileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(`${file.name} is not an image`);
      return;
    }
    setUploadingMobile(true);
    const optimizedFile = await optimizeHeroImage(file, 'mobile');
    const url = await uploadImageFile(optimizedFile, 'hero-mobile');
    if (url) onUpdate({ ...slide, mobile: url });
    setUploadingMobile(false);
    if (e.target) e.target.value = '';
  };

  const handleDesktopUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(`${file.name} is not an image`);
      return;
    }
    setUploadingDesktop(true);
    const optimizedFile = await optimizeHeroImage(file, 'desktop');
    const url = await uploadImageFile(optimizedFile, 'hero-desktop');
    if (url) onUpdate({ ...slide, desktop: url });
    setUploadingDesktop(false);
    if (e.target) e.target.value = '';
  };

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      <div className="flex items-center gap-2 p-3 border-b bg-muted/30">
        <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="font-medium text-sm">Slide {index + 1}</span>
        <div className="flex gap-1 ml-auto">
          {index > 0 && (
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onMoveUp}>
              <ArrowUp className="h-3.5 w-3.5" />
            </Button>
          )}
          {index < total - 1 && (
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onMoveDown}>
              <ArrowDown className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={onRemove}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          {/* Mobile Image */}
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Portrait (Mobile)</Label>
            <div
              className="aspect-[9/16] rounded-lg border-2 border-dashed border-muted-foreground/20 hover:border-primary/50 transition-colors cursor-pointer relative overflow-hidden bg-muted/30 flex items-center justify-center"
              onClick={() => mobileInputRef.current?.click()}
            >
              {uploadingMobile ? (
                <LoadingSpinner />
              ) : slide.mobile ? (
                <MediaImage src={slide.mobile} alt="" className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="flex flex-col items-center gap-1 text-muted-foreground">
                  <Upload className="h-6 w-6" />
                  <span className="text-xs">Portrait</span>
                </div>
              )}
            </div>
            <input
              ref={mobileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleMobileUpload}
              disabled={uploadingMobile}
            />
          </div>

          {/* Desktop Image */}
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Landscape (Desktop)</Label>
            <div
              className="aspect-[16/9] rounded-lg border-2 border-dashed border-muted-foreground/20 hover:border-primary/50 transition-colors cursor-pointer relative overflow-hidden bg-muted/30 flex items-center justify-center"
              onClick={() => desktopInputRef.current?.click()}
            >
              {uploadingDesktop ? (
                <LoadingSpinner />
              ) : slide.desktop ? (
                <MediaImage src={slide.desktop} alt="" className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="flex flex-col items-center gap-1 text-muted-foreground">
                  <Upload className="h-6 w-6" />
                  <span className="text-xs">Landscape</span>
                </div>
              )}
            </div>
            <input
              ref={desktopInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleDesktopUpload}
              disabled={uploadingDesktop}
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <Label className="text-xs text-muted-foreground mb-1 block">Slide Description (optional)</Label>
          <Input
            placeholder="e.g. 'Raising Leaders That Transform Society'"
            value={slide.description || ''}
            onChange={(e) => onUpdate({ ...slide, description: e.target.value })}
            className="text-sm"
          />
        </div>
      </div>
    </div>
  );
}

export default function AdminHeroManagerPage() {
  const [heroData, setHeroData] = useState<HeroData>({ ...DEFAULT_HERO });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const fetchHero = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<{ hero: HeroData | null }>('/api/admin/site-content/hero');
      if (response.success && response.data?.hero) {
        const h = response.data.hero;
        setHeroData({
          slides: Array.isArray(h.slides) ? h.slides : [],
          rotationIntervalMs: h.rotationIntervalMs || 6000,
          overlayOpacity: h.overlayOpacity ?? 0.3,
          isEnabled: h.isEnabled ?? true,
        });
      } else {
        setHeroData({ ...DEFAULT_HERO });
      }
    } catch {
      setError('Failed to fetch hero configuration');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHero();
  }, [fetchHero]);

  const handleSave = async () => {
    if (heroData.isEnabled && heroData.slides.length === 0) {
      toast.error('Add at least one slide, or disable the hero section before saving');
      return;
    }
    for (const slide of heroData.slides) {
      if (!slide.mobile || !slide.desktop) {
        toast.error('Each slide needs both a portrait and landscape image');
        return;
      }
    }

    setIsSaving(true);
    try {
      const response = await api.put('/api/admin/site-content/hero', {
        slides: heroData.slides,
        rotationIntervalMs: heroData.rotationIntervalMs,
        overlayOpacity: heroData.overlayOpacity,
        isEnabled: heroData.isEnabled,
      });

      if (response.success) {
        toast.success('Hero configuration saved');
        fetchHero();
      } else {
        toast.error(response.error?.message || 'Failed to save');
      }
    } catch {
      toast.error('Failed to save hero configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setHeroData({ ...DEFAULT_HERO });
    toast.info('Reset to defaults — hero is disabled, click Save to apply');
  };

  const addSlide = () => {
    if (heroData.slides.length >= MAX_SLIDES) {
      toast.error(`Maximum ${MAX_SLIDES} slides allowed`);
      return;
    }
    setHeroData((prev) => ({
      ...prev,
      slides: [...prev.slides, { mobile: '', desktop: '', description: '' }],
    }));
  };

  const removeSlide = (index: number) => {
    setHeroData((prev) => ({
      ...prev,
      slides: prev.slides.filter((_, i) => i !== index),
    }));
  };

  const updateSlide = (index: number, updated: HeroSlide) => {
    setHeroData((prev) => {
      const slides = [...prev.slides];
      slides[index] = updated;
      return { ...prev, slides };
    });
  };

  const moveSlide = (index: number, direction: 'up' | 'down') => {
    setHeroData((prev) => {
      const slides = [...prev.slides];
      const swapIndex = direction === 'up' ? index - 1 : index + 1;
      if (swapIndex < 0 || swapIndex >= slides.length) return prev;
      [slides[index], slides[swapIndex]] = [slides[swapIndex], slides[index]];
      return { ...prev, slides };
    });
  };

  const previewSlide = heroData.slides[0];

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-24">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Hero Manager</h1>
            <p className="text-muted-foreground">
              Manage hero slides — each slide needs a portrait (mobile) and landscape (desktop) image
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setPreviewOpen(true)} disabled={heroData.slides.length === 0}>
              <Eye className="mr-2 h-4 w-4" />
              Preview
            </Button>
            <Button variant="outline" onClick={handleReset}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              <Save className="mr-2 h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        {error && (
          <div className="text-center py-4 text-destructive bg-destructive/10 rounded-lg">
            {error}
          </div>
        )}

        {/* Slides */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Slides</h2>
              <p className="text-sm text-muted-foreground">
                {heroData.slides.length}/{MAX_SLIDES} slides — upload a portrait and landscape image per slide
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={addSlide} disabled={heroData.slides.length >= MAX_SLIDES}>
              + Add Slide
            </Button>
          </div>

          {heroData.slides.length === 0 ? (
            <div className="border-2 border-dashed rounded-lg p-12 text-center">
              <p className="text-muted-foreground mb-4">No slides yet. Add a slide to get started.</p>
              <Button onClick={addSlide} disabled={heroData.slides.length >= MAX_SLIDES}>
                + Add Slide
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {heroData.slides.map((slide, i) => (
                <SlideCard
                  key={i}
                  slide={slide}
                  index={i}
                  total={heroData.slides.length}
                  onUpdate={(updated) => updateSlide(i, updated)}
                  onRemove={() => removeSlide(i)}
                  onMoveUp={() => moveSlide(i, 'up')}
                  onMoveDown={() => moveSlide(i, 'down')}
                />
              ))}
            </div>
          )}
        </div>

        {/* Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Settings</CardTitle>
            <CardDescription>Configure slideshow behavior</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Label>Enabled</Label>
                <p className="text-sm text-muted-foreground">Show managed hero instead of defaults</p>
              </div>
              <Switch
                checked={heroData.isEnabled}
                onCheckedChange={(checked) => setHeroData((prev) => ({ ...prev, isEnabled: checked }))}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Rotation Interval</Label>
                <Badge variant="outline">{(heroData.rotationIntervalMs / 1000).toFixed(1)}s</Badge>
              </div>
              <Slider
                value={[heroData.rotationIntervalMs]}
                min={1000}
                max={15000}
                step={500}
                onValueChange={([v]) => setHeroData((prev) => ({ ...prev, rotationIntervalMs: v }))}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>1s</span>
                <span>15s</span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Overlay Opacity</Label>
                <Badge variant="outline">{Math.round(heroData.overlayOpacity * 100)}%</Badge>
              </div>
              <Slider
                value={[heroData.overlayOpacity]}
                min={0}
                max={1}
                step={0.05}
                onValueChange={([v]) => setHeroData((prev) => ({ ...prev, overlayOpacity: v }))}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Transparent</span>
                <span>Full black</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Hero Preview</DialogTitle>
            <DialogDescription>Preview of the hero section</DialogDescription>
          </DialogHeader>
          {previewSlide && (
            <div className="space-y-4">
              <div className="relative rounded-lg overflow-hidden bg-black">
                <div className="hidden md:block aspect-video relative">
                  <MediaImage src={previewSlide.desktop} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white">
                      <p className="text-2xl font-bold mb-2">Raising Leaders That Transform Society</p>
                      <p className="text-lg opacity-80">Dr. David Ogbueli & Dominion Mandate</p>
                    </div>
                  </div>
                </div>
                <div className="md:hidden aspect-[9/16] relative max-w-64 mx-auto">
                  <MediaImage src={previewSlide.mobile} alt="" className="absolute inset-0 w-full h-full object-cover" />
                </div>
              </div>
              {previewSlide.description && (
                <p className="text-sm text-muted-foreground italic">"{previewSlide.description}"</p>
              )}
              <p className="text-sm text-muted-foreground">
                {heroData.slides.length} slide{heroData.slides.length !== 1 ? 's' : ''} · {(heroData.rotationIntervalMs / 1000).toFixed(1)}s rotation
              </p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
